/* Form Component to Add or Edit Customer details*/

import { useContext, useEffect, useState } from "react";
import { Button, Form, Input, Select, Row, Col, TimePicker, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { FormRow } from "../../../../components/form/form-row";
import {
  TDropdownOptions,
  TPrivilege,
  TResponseModel,
} from "../../../../types/config";
import { ePrivileges, eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { TCustomer, TServicePartner } from "../../../../types";
import {
  AddEditCustomer,
  GetServicePartnerList,
  GetSpecificCustomerData,
} from "../../../../utils/api.constant";
import Utils from "../../../../utils";
import dayjs from "dayjs";
import { UserContext } from "../../../../context";
import "../../../../index.css";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * CustomerForm component is used for adding or editing customer information.
 * It provides a form with fields for customer details such as name, code,
 * address, country, state, region, district, and zipcode. The component
 * handles fetching and setting dropdown options for countries, states,
 * cities, and regions based on the selected values. It also manages form
 * submission to add or update customer records.
 *
 * Props:
 * - id: number - The ID of the customer to edit; 0 for adding a new customer.
 * - onCloseDrawer: function - Callback function to close the form drawer.
 * - onRefreshList: function - Callback function to refresh the customer list
 *   after adding or editing a customer.
 */
export default function CustomerForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPartners, setSelectedPartners] = useState<TServicePartner[]>(
    []
  );
  const [servicePartnerOptions, setservicePartnerOptions] = useState<
    TServicePartner[]
  >([]);
  const context: any = useContext(UserContext);

  const { post } = useFetch();
  const { onShowToast } = useToast();

  const defaultValues: TCustomer = {
    id: 0,
    name: "",
    customerCode: "",
  };

  // Hook to handle on load operations
  useEffect(() => {
    /**
     * fetchData is an asynchronous function that fetches customer data, countries, states, and regions.
     * It first fetches specific customer data if ID is provided, followed by fetching countries, then states, and finally regions.
     */
    const fetchData = async () => {
      await fetchSpecificCustomer(); // Fetch specific customer if ID is provided
      await fetchServicePartners();
    };

    fetchData(); // Call the fetch function
  }, []);

  // Handle on submit event
  const onFinish = async (values: TCustomer) => {
    try {
      const payload = {
        data: {
          id: props.id ? props.id : 0,
          servicePartners: selectedPartners,
          ...values,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditCustomer, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        props.onCloseDrawer();
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        setSelectedPartners([]);
        props.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const fetchSpecificCustomer = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response: TResponseModel = await post(
          GetSpecificCustomerData,
          payload
        );
        if (response) {
          const { data, dataResponse } = response;
          const { returnCode, description } = dataResponse;
          if (returnCode === eResultCode.SUCCESS) {
            const { dayStartTime, dayClosingTime, ...restValues } = data;
            form.setFieldValue("dayStartTime", dayjs(dayStartTime));
            form.setFieldValue("dayClosingTime", dayjs(dayClosingTime));
            form.setFieldsValue({ ...restValues });
            form.setFieldValue(
              "servicePartnerIds",
              data?.servicePartners.map(
                (partner: TServicePartner) => partner.id
              )
            );
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.centerTop,
              content: description,
            });
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  /**
   * Fetches the list of service partners from the API.
   * Sets the isLoading state to true, sends a request with the current filterParams, and sets the servicePartnerOptions state with the response.
   * If the response is not successful, shows an error toast.
   * Finally, sets isLoading to false.
   */
  const fetchServicePartners = async () => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(
        GetServicePartnerList,
        payload
      );
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        setservicePartnerOptions(Utils.mapDropdownOptions(response.data));
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
    setIsLoading(false);
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Customer`}
                </h2>
              </Col>
            </Space>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("Enter name"),
                  },
                ]}
              >
                <Input autoComplete="off" placeholder="Enter customer name" />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="customerCode"
                label="Code"
                rules={[
                  { required: true, message: "Please enter code" },
                  {
                    pattern: /^[a-zA-Z0-9]{3}$/, // Regular expression for exactly 3 alphanumeric characters
                    message: "Code must be 3 alphanumeric characters",
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  disabled={
                    Utils.isUserHasAccess(
                      (context?.user?.privilegeList as TPrivilege[]) || [],
                      ePrivileges.EDITCUSTCODE
                    )
                      ? false
                      : true
                  }
                  placeholder="Enter code"
                  maxLength={3}
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase(); // Convert input to uppercase
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="servicePartnerIds"
                label="Service Partners"
                // rules={[{ required: true, message: "Please choose district" }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Please select service partners"
                  // value={selectedPartners}
                  onChange={(newValue, option) => {
                    setSelectedPartners(
                      (option as any[])?.map(
                        (item: TDropdownOptions) => item?.data
                      )
                    );
                  }}
                  filterOption={(input, option: any) => {
                    return option?.label
                      ? option.label.toLowerCase().includes(input.toLowerCase())
                      : false;
                  }}
                  options={servicePartnerOptions}
                ></Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="dayStartTime"
                label="Day Start Time"
                rules={[
                  { required: true, message: "Please select day start time" },
                ]}
              >
                <TimePicker
                  disabled={
                    Utils.isUserHasAccess(
                      (context?.user?.privilegeList as TPrivilege[]) || [],
                      ePrivileges.EDITDAYSTARTEND
                    )
                      ? false
                      : true
                  }
                  format="HH:mm:ss"
                  placeholder="Select start time"
                  use12Hours={false}
                  style={{ width: "100%" }}
                  onChange={(time, timeString) => {
                    form.setFieldValue("dayStartTime", time);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="dayClosingTime"
                label="Day Closing Time"
                rules={[
                  { required: true, message: "Please select day closing time" },
                ]}
              >
                <TimePicker
                  disabled={
                    Utils.isUserHasAccess(
                      (context?.user?.privilegeList as TPrivilege[]) || [],
                      ePrivileges.EDITDAYSTARTEND
                    )
                      ? false
                      : true
                  }
                  format="HH:mm:ss"
                  placeholder="Select closing time"
                  use12Hours={false}
                  style={{ width: "100%" }}
                  onChange={(time, timeString) => {
                    form.setFieldValue("dayClosingTime", time);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              className="btnDimensions"
            >
              {props.id ? "Update" : "Save"}
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
